import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

const PAGE_SIZE = 1000

export interface ListingsReportStats {
  total: number
  withAd: number
  withSubtitles: number
  withBsl: number
  /** At least one of AD, subtitles, or BSL */
  withAnyAccessibility: number
  /** None of AD, subtitles, BSL */
  withNoAccessibility: number
  fromXml: number
  fromOverlay: number
}

function emptyStats(): ListingsReportStats {
  return {
    total: 0,
    withAd: 0,
    withSubtitles: 0,
    withBsl: 0,
    withAnyAccessibility: 0,
    withNoAccessibility: 0,
    fromXml: 0,
    fromOverlay: 0,
  }
}

function addRowToStats(s: ListingsReportStats, row: TvListingReportRow) {
  s.total += 1
  if (row.has_ad) s.withAd += 1
  if (row.has_subtitles) s.withSubtitles += 1
  if (row.has_bsl) s.withBsl += 1
  const any = Boolean(row.has_ad || row.has_subtitles || row.has_bsl)
  if (any) s.withAnyAccessibility += 1
  else s.withNoAccessibility += 1
  if (row.source === 'xml') s.fromXml += 1
  else if (row.source === 'overlay') s.fromOverlay += 1
}

interface TvListingReportRow {
  channel_id: string
  start_at: string
  has_ad: boolean
  has_subtitles: boolean
  has_bsl: boolean
  source: string
}

function listingsWindowUtc(): { startIso: string; endIso: string } {
  const now = new Date()
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0, 0))
  const end = new Date(start)
  end.setUTCDate(end.getUTCDate() + 7)
  return { startIso: start.toISOString(), endIso: end.toISOString() }
}

async function fetchAllListingsInWindow(startIso: string, endIso: string): Promise<TvListingReportRow[]> {
  const rows: TvListingReportRow[] = []
  let from = 0
  for (;;) {
    const { data, error } = await supabase
      .from('tv_listings')
      .select('channel_id, start_at, has_ad, has_subtitles, has_bsl, source')
      .gte('start_at', startIso)
      .lt('start_at', endIso)
      .order('start_at', { ascending: true })
      .range(from, from + PAGE_SIZE - 1)

    if (error) throw error
    const batch = (data ?? []) as TvListingReportRow[]
    if (batch.length === 0) break
    rows.push(...batch)
    if (batch.length < PAGE_SIZE) break
    from += PAGE_SIZE
  }
  return rows
}

async function fetchChannelDisplayNames(): Promise<Map<string, string>> {
  const { data, error } = await supabase
    .from('listing_channel_display')
    .select('channel_id, name')
    .eq('is_displayed', true)

  if (error) throw error
  const map = new Map<string, string>()
  for (const r of data ?? []) {
    if (r.channel_id && r.name) map.set(r.channel_id, r.name)
  }
  return map
}

export interface ListingsReportChannelRow {
  channelId: string
  displayName: string
  stats: ListingsReportStats
}

export interface ListingsReportData {
  windowLabel: string
  startIso: string
  endIso: string
  overall: ListingsReportStats
  byDay: { dayKey: string; label: string; stats: ListingsReportStats }[]
  byChannel: ListingsReportChannelRow[]
  /** Channel breakdown per calendar day (UTC date key yyyy-MM-dd), sorted by total descending */
  byChannelByDayUtc: Record<string, ListingsReportChannelRow[]>
  rowCount: number
}

export const ADMIN_LISTINGS_REPORT_QUERY_KEY = ['admin', 'listings-report'] as const

export function useAdminListingsReport(
  enabled: boolean,
  startDate?: string,
  endDateInclusive?: string
) {
  return useQuery({
    queryKey: [...ADMIN_LISTINGS_REPORT_QUERY_KEY, startDate ?? null, endDateInclusive ?? null],
    enabled,
    staleTime: 60_000,
    queryFn: async (): Promise<ListingsReportData> => {
      let startIso: string
      let endIso: string
      let windowLabel: string
      if (startDate && endDateInclusive) {
        const startUtc = new Date(`${startDate}T00:00:00.000Z`)
        const endExclusiveUtc = new Date(`${endDateInclusive}T00:00:00.000Z`)
        endExclusiveUtc.setUTCDate(endExclusiveUtc.getUTCDate() + 1)
        startIso = startUtc.toISOString()
        endIso = endExclusiveUtc.toISOString()
        windowLabel = `${startDate} → ${endDateInclusive} (UTC, inclusive)`
      } else {
        const d = listingsWindowUtc()
        startIso = d.startIso
        endIso = d.endIso
        windowLabel = `${startIso.slice(0, 10)} → ${endIso.slice(0, 10)} (UTC)`
      }

      const [rows, channelNames] = await Promise.all([fetchAllListingsInWindow(startIso, endIso), fetchChannelDisplayNames()])

      const overall = emptyStats()
      const byDayMap = new Map<string, ListingsReportStats>()
      const byChannelMap = new Map<string, ListingsReportStats>()
      const byDayChannelMap = new Map<string, Map<string, ListingsReportStats>>()

      for (const row of rows) {
        addRowToStats(overall, row)

        const dayKey = row.start_at.slice(0, 10)
        let dayStats = byDayMap.get(dayKey)
        if (!dayStats) {
          dayStats = emptyStats()
          byDayMap.set(dayKey, dayStats)
        }
        addRowToStats(dayStats, row)

        let chStats = byChannelMap.get(row.channel_id)
        if (!chStats) {
          chStats = emptyStats()
          byChannelMap.set(row.channel_id, chStats)
        }
        addRowToStats(chStats, row)

        let dayChMap = byDayChannelMap.get(dayKey)
        if (!dayChMap) {
          dayChMap = new Map()
          byDayChannelMap.set(dayKey, dayChMap)
        }
        let dayChStats = dayChMap.get(row.channel_id)
        if (!dayChStats) {
          dayChStats = emptyStats()
          dayChMap.set(row.channel_id, dayChStats)
        }
        addRowToStats(dayChStats, row)
      }

      const byDay = Array.from(byDayMap.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([dayKey, stats]) => ({
          dayKey,
          label: dayKey,
          stats,
        }))

      const mapToChannelRows = (m: Map<string, ListingsReportStats>): ListingsReportChannelRow[] =>
        Array.from(m.entries())
          .map(([channelId, stats]) => ({
            channelId,
            displayName: channelNames.get(channelId) ?? channelId,
            stats,
          }))
          .sort((a, b) => b.stats.total - a.stats.total)

      const byChannel = mapToChannelRows(byChannelMap)

      const byChannelByDayUtc: Record<string, ListingsReportChannelRow[]> = {}
      for (const [dayKey, chMap] of byDayChannelMap) {
        byChannelByDayUtc[dayKey] = mapToChannelRows(chMap)
      }

      return {
        windowLabel,
        startIso,
        endIso,
        overall,
        byDay,
        byChannel,
        byChannelByDayUtc,
        rowCount: rows.length,
      }
    },
  })
}

export interface OverlayReportStats {
  total: number
  withAd: number
  withSubtitles: number
  withBsl: number
  withAnyAccessibility: number
  withNoAccessibility: number
}

function emptyOverlayStats(): OverlayReportStats {
  return {
    total: 0,
    withAd: 0,
    withSubtitles: 0,
    withBsl: 0,
    withAnyAccessibility: 0,
    withNoAccessibility: 0,
  }
}

interface AccessibilityOverlayReportRow {
  channel_id: string
  start_at: string
  has_ad: boolean
  has_subtitles: boolean
  has_bsl: boolean
}

function addOverlayRowToStats(s: OverlayReportStats, row: AccessibilityOverlayReportRow) {
  s.total += 1
  if (row.has_ad) s.withAd += 1
  if (row.has_subtitles) s.withSubtitles += 1
  if (row.has_bsl) s.withBsl += 1
  const any = Boolean(row.has_ad || row.has_subtitles || row.has_bsl)
  if (any) s.withAnyAccessibility += 1
  else s.withNoAccessibility += 1
}

async function fetchAllOverlaysInWindow(startIso: string, endIso: string): Promise<AccessibilityOverlayReportRow[]> {
  const rows: AccessibilityOverlayReportRow[] = []
  let from = 0
  for (;;) {
    const { data, error } = await supabase
      .from('accessibility_overlays')
      .select('channel_id, start_at, has_ad, has_subtitles, has_bsl')
      .gte('start_at', startIso)
      .lt('start_at', endIso)
      .order('start_at', { ascending: true })
      .range(from, from + PAGE_SIZE - 1)

    if (error) throw error
    const batch = (data ?? []) as AccessibilityOverlayReportRow[]
    if (batch.length === 0) break
    rows.push(...batch)
    if (batch.length < PAGE_SIZE) break
    from += PAGE_SIZE
  }
  return rows
}

export interface AccessibilityOverlaysReportData {
  windowLabel: string
  startIso: string
  endIso: string
  overall: OverlayReportStats
  byDay: { dayKey: string; stats: OverlayReportStats }[]
  byChannel: { channelId: string; displayName: string; stats: OverlayReportStats }[]
  rowCount: number
}

export function useAdminAccessibilityOverlaysReport(
  enabled: boolean,
  startDate: string,
  endDateInclusive: string
) {
  return useQuery({
    queryKey: ['admin', 'accessibility-overlays-report', startDate, endDateInclusive],
    enabled,
    staleTime: 60_000,
    queryFn: async (): Promise<AccessibilityOverlaysReportData> => {
      const startUtc = new Date(`${startDate}T00:00:00.000Z`)
      const endExclusiveUtc = new Date(`${endDateInclusive}T00:00:00.000Z`)
      endExclusiveUtc.setUTCDate(endExclusiveUtc.getUTCDate() + 1)
      const startIso = startUtc.toISOString()
      const endIso = endExclusiveUtc.toISOString()
      const [rows, channelNames] = await Promise.all([fetchAllOverlaysInWindow(startIso, endIso), fetchChannelDisplayNames()])

      const overall = emptyOverlayStats()
      const byDayMap = new Map<string, OverlayReportStats>()
      const byChannelMap = new Map<string, OverlayReportStats>()

      for (const row of rows) {
        addOverlayRowToStats(overall, row)

        const dayKey = row.start_at.slice(0, 10)
        let dayStats = byDayMap.get(dayKey)
        if (!dayStats) {
          dayStats = emptyOverlayStats()
          byDayMap.set(dayKey, dayStats)
        }
        addOverlayRowToStats(dayStats, row)

        let chStats = byChannelMap.get(row.channel_id)
        if (!chStats) {
          chStats = emptyOverlayStats()
          byChannelMap.set(row.channel_id, chStats)
        }
        addOverlayRowToStats(chStats, row)
      }

      const byDay = Array.from(byDayMap.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([dayKey, stats]) => ({ dayKey, stats }))

      const byChannel = Array.from(byChannelMap.entries())
        .map(([channelId, stats]) => ({
          channelId,
          displayName: channelNames.get(channelId) ?? channelId,
          stats,
        }))
        .sort((a, b) => b.stats.total - a.stats.total)

      return {
        windowLabel: `${startDate} → ${endDateInclusive} (UTC, inclusive)`,
        startIso,
        endIso,
        overall,
        byDay,
        byChannel,
        rowCount: rows.length,
      }
    },
  })
}
